'use client'
import React, { useEffect, useRef, useMemo } from 'react'
import type { ICinematics } from '@/types'
import { resolveThemeStyles } from '@/cinematics/utils/resolveThemeStyles'
import { useRouteChange } from '@/hooks/utils/useRouteChange'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { useUserIdentified } from '@/hooks/utils/useUserIdentified'
import { useGeolocation } from '@/hooks/geolocation/useGeolocation'
import { useDevice } from '@/hooks/utils/useDevice'
import { useEligibleBrandingTag } from '@/hooks/cinematics/useEligibleBrandingTag'
import { useBrandingLayer } from '@/hooks/cinematics/useBrandingLayer'
import { BrandingLayerProvider, useBrandingLayerContext } from './BrandingLayerContext'
import { usePaletteTransition } from '@/hooks/cinematics/usePaletteTransition'
import { useBrandingReveal } from '@/hooks/cinematics/useBrandingReveal'
import { useTeamGradientReveal } from '@/hooks/cinematics/useTeamGradientReveal'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { useBranding } from '@/hooks/cinematics/useBranding'
import { useABDecisionState } from './OptimizelyProvider'
import { hasBrandingConfig } from '@/utils/branding'
import { computeAppLoadingState } from '@/cinematics/utils/appLoadingState'
import { clarity, CLARITY_TAGS } from '@/utils/clarity'

import settings from '@/settings/public'

const appCode = settings.app.code ?? ''
const hasBranding = hasBrandingConfig(appCode)

interface AppInitializerProps {
  children: React.ReactNode
  isIdentified: boolean
  isLoadingUser: boolean
  isLoggedIn: boolean
  eligibleBrandingTag: string | null
  isLoadingBrandingTag: boolean
}

const AppInitializer = (props: AppInitializerProps) => {
  const { children, isIdentified, isLoadingUser, isLoggedIn, eligibleBrandingTag, isLoadingBrandingTag } = props

  const { isApplied: isABApplied } = useABDecisionState()

  useDevice()

  // Branding waits for AB to finish so it merges on top of the correct base.
  // Eligible branding always overrides the AB decision.
  const { isLoading: isBrandingLoading, isApplied: isBrandingApplied } = useBranding({
    brandingTag: eligibleBrandingTag || '',
    enabled: !!eligibleBrandingTag,
    revertToOriginal: !isLoggedIn
  })

  // Cross-dissolve the branded page in as the overlay fades out; no-op for
  // non-branded sessions (tag null).
  useBrandingReveal(!!eligibleBrandingTag && isBrandingApplied)

  const { isLoading, backgroundIntensity } = computeAppLoadingState({
    isABApplied,
    isLoadingUser,
    isIdentified,
    isLoggedIn,
    hasBranding,
    isLoadingBrandingTag,
    eligibleBrandingTag: eligibleBrandingTag ?? null,
    isBrandingLoading,
  })

  // Cross-dissolve the page when the favorite-team color changes mid-session
  // (user picks/switches their team). Gated on the overlay being gone so the
  // initial load resolution — which happens behind the overlay — never flashes.
  const layer = useBrandingLayerContext()
  const teamColor = layer?.kind === 'team' ? layer.theme.color : undefined
  useTeamGradientReveal(teamColor, !isLoading)

  return (
    <>
      <LoadingOverlay
        isLoading={isLoading}
        backgroundIntensity={backgroundIntensity}
        // Tag present → branding fetch/merge keeps the overlay up long enough
        // on its own; the min-hold only guards the fast unbranded path
        // against flashing. (Inverting this never arms the hold: the tag is
        // still null at show time, when LoadingOverlay evaluates minVisibleMs.)
        minVisibleMs={eligibleBrandingTag ? 0 : 650}
      />
      {children}
    </>
  )
}

export const AppProvider = (props: {
  children: React.ReactNode
  cinematics: ICinematics
  setPath: (path: string) => void
}) => {
  const { children, cinematics, setPath } = props
  const { sendGtm } = useTagManager()
  const { country, isLoading: isGeoLoading, isError: isGeoError } = useGeolocation()
  const ambiance = cinematics?.ambiance || {}

  const { isIdentified, isLoading: isLoadingUser, isLoggedIn, profile } = useUserIdentified({ withIntercom: settings?.intercom?.enabled })

  // Branding tag derives from the shared active-orders cache (dehydrated from
  // SSR); a purchase invalidating ['orders'] re-derives it automatically.
  const { eligibleBrandingTag, isLoading: isLoadingBrandingTag } = useEligibleBrandingTag({
    enabled: isIdentified && isLoggedIn,
  })

  // Same compound gate computeAppLoadingState already uses for waitingForOrders:
  // on apps without branding config, the orders query never enables, so bare
  // isLoadingBrandingTag would stay stuck true forever.
  const isBrandingResolving = isLoggedIn && hasBranding && isLoadingBrandingTag
  const layer = useBrandingLayer({ eligibleBrandingTag: eligibleBrandingTag ?? null, isBrandingResolving, profile })

  const colorStyles = useMemo(
    () => resolveThemeStyles({ ambiance: { palette: ambiance?.palette || {} }, layer }),
    [ambiance?.palette, layer],
  )

  usePaletteTransition(colorStyles)

  const initialPageViewSent = useRef(false)
  const currentPath = useRef<string>('')

  useEffect(() => {
    clarity.set(CLARITY_TAGS.APP_CODE, settings.app.code ?? '')
  }, [])

  // Route change detection
  useRouteChange((path) => {
    setPath(path)
    currentPath.current = path
    // For subsequent navigations, geolocation is already loaded
    if (initialPageViewSent.current) {
      sendGtm.PAGE_VIEW({ path })
    }
  })

  // Send page view event on initial page load. Fire once geolocation has settled
  // (resolved OR errored) — a geo failure must not suppress the entire session's
  // page views. On error the GTM payload simply omits country.
  useEffect(() => {
    if (!isGeoLoading && (country || isGeoError) && currentPath.current && !initialPageViewSent.current) {
      initialPageViewSent.current = true
      sendGtm.PAGE_VIEW({ path: currentPath.current })
    }
  }, [isGeoLoading, country, isGeoError, sendGtm])

  return (
    <BrandingLayerProvider value={layer}>
      <style>{`html { ${colorStyles} }`}</style>
      <AppInitializer
        isIdentified={isIdentified}
        isLoadingUser={isLoadingUser}
        isLoggedIn={isLoggedIn}
        eligibleBrandingTag={eligibleBrandingTag ?? null}
        isLoadingBrandingTag={isLoadingBrandingTag}
      >
        {children}
      </AppInitializer>
    </BrandingLayerProvider>
  )
}